// Adding or editing one person, shared by the People page and by a single
// workshop's team.
//
// The role's access policy decides which scope question gets asked — a
// workshop, a region, or a set of them — so the form follows the role rather
// than showing every field and hoping the right ones are filled.
//
// Someone with no login is a workshop stakeholder or a service advisor: a
// contact row, not an account. There is no access to scope, so the form asks
// only who they are, where they work and how they are reached — and their
// email is editable, because giving them one is what creates their login.

import { FieldError, FieldLabel, Select, inputClass } from "./primitives";
import { FormDialog } from "./OrgKit";
import { usePersonForm } from "./useOrgEditors";

/**
 * @param person   the login being edited, or null to add
 * @param atWorkshop  pins a new person to this workshop and hides the picker
 *                    — used when adding from inside one workshop's team
 */
export default function PersonDialog({ open, onOpenChange, tenantId, ix, person, atWorkshop }) {
  const {
    form,
    setForm,
    errors,
    toggleId,
    setRole,
    submit,
    pending,
    noLogin,
    question,
    askWorkshop,
    emailOptional,
    roleIsFixed,
  } = usePersonForm({ open, onOpenChange, tenantId, ix, person, atWorkshop });

  const checkboxList = (items, labelOf) => (
    <div className="flex max-h-[200px] flex-col gap-1 overflow-y-auto rounded-xl bg-[#F9FAFB] p-2">
      {items.map((it) => (
        <label
          key={it.id}
          className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 text-[14px] text-[#111827] hover:bg-white"
        >
          <input
            type="checkbox"
            className="h-4 w-4 rounded accent-[#2667B5]"
            checked={form.scope_ids.includes(it.id)}
            onChange={() => toggleId(it.id)}
          />
          {labelOf(it)}
        </label>
      ))}
    </div>
  );

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={person ? `Edit ${person.name || person.email}` : "Add person"}
      // Only what the person needs to know to act, not what the thing is.
      description={
        person ? null : atWorkshop ? `Added to ${atWorkshop}.` : "They set their own password at sign-up."
      }
      onSubmit={submit}
      submitLabel={person ? "Save changes" : "Add person"}
      pending={pending}
    >
      <div>
        <FieldLabel>{emailOptional ? "Email (optional — gives them a login)" : "Email"}</FieldLabel>
        <input
          type="email"
          className={inputClass}
          value={form.email}
          placeholder="name@brand.in"
          autoFocus={!person}
          // The email identifies the login, so changing it would be a
          // different person. Someone who has none yet can still be given
          // one, which is what provisions their account.
          disabled={!!person && !noLogin}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
        <FieldError>{errors.email}</FieldError>
      </div>

      <div>
        <FieldLabel>Name</FieldLabel>
        <input
          type="text"
          className={inputClass}
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
      </div>

      <div>
        <FieldLabel>Phone number</FieldLabel>
        <input
          type="tel"
          className={inputClass}
          value={form.number}
          placeholder="+91 98765 43210"
          onChange={(e) => setForm({ ...form, number: e.target.value })}
        />
        <FieldError>{errors.number}</FieldError>
      </div>

      <div>
        <FieldLabel>Role</FieldLabel>
        <Select
          value={form.role}
          disabled={roleIsFixed}
          onChange={(e) => setRole(e.target.value)}
        >
          {ix.roles.map((r) => (
            <option key={r.code} value={r.code}>
              {r.label}
            </option>
          ))}
        </Select>
        <FieldError>{errors.role}</FieldError>
      </div>

      {question && (
        <div>
          <FieldLabel>
            {askWorkshop && "Works at"}
            {question === "region" && "Covers region"}
            {question === "regions" && "Covers regions"}
            {question === "dealers" && "Covers dealers"}
            {question === "workshops" && "Covers workshops"}
            {question === "workshop" && atWorkshop && "Works at"}
          </FieldLabel>
          {question === "workshop" &&
            (atWorkshop ? (
              <p className="text-[14px] text-[#111827]">{atWorkshop}</p>
            ) : (
              <Select
                value={form.workshop_location}
                onChange={(e) => setForm({ ...form, workshop_location: e.target.value })}
              >
                <option value="">Pick a workshop</option>
                {ix.workshops.map((w) => (
                  <option key={w.id} value={w.workshop_location}>
                    {w.workshop_location}
                  </option>
                ))}
              </Select>
            ))}
          {question === "region" && (
            <Select value={form.scope_id} onChange={(e) => setForm({ ...form, scope_id: e.target.value })}>
              <option value="">Pick a region</option>
              {ix.regions.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </Select>
          )}
          {question === "regions" && checkboxList(ix.regions, (r) => r.name)}
          {question === "dealers" && checkboxList(ix.dealers, (d) => d.name)}
          {question === "workshops" && checkboxList(ix.workshops, (w) => w.workshop_location)}
          <FieldError>{errors.scope}</FieldError>
        </div>
      )}
    </FormDialog>
  );
}
